'use client';

import { createContext, useContext, ReactNode, useMemo, useCallback } from 'react';
import { useOrganization } from './organization-context';

interface CurrencyContextType {
  baseCurrency: string;
  refreshCurrency: () => Promise<void>;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const { organization, refreshOrganization } = useOrganization();

  // Get base currency directly from organization context (single source of truth)
  const baseCurrency = organization?.base_currency || 'EUR';

  // Refresh currency by refreshing organization context
  const refreshCurrency = useCallback(async () => {
    await refreshOrganization();
  }, [refreshOrganization]);

  const contextValue = useMemo(
    () => ({ baseCurrency, refreshCurrency }),
    [baseCurrency, refreshCurrency]
  );

  return <CurrencyContext.Provider value={contextValue}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
